(() => {
  const READY_FLAG = 'a7FilterReady';
  const normalise = (v) => String(v ?? '').trim().toLowerCase();

  const canonicalStiffness = (v) => {
    const n = normalise(v);
    if (n.includes('rigor')) return 'Rigor Mortis';
    if (n === 'stiff' || n.includes('stiff')) return 'Stiff';
    if (n === 'mids' || n.includes('medium')) return 'Mids';
    if (n === 'flex' || n === 'flexi' || n.includes('flex')) return 'Flexi';
    return String(v || '').trim();
  };

  const canonicalSize = (v) => {
    let s = String(v || '').trim();
    s = s.replace(/^((men|women)(['’]?)s|unisex)\s+/i, '');
    return s.toUpperCase();
  };

  const colourTagMap = {
    black:'black', white:'white', grey:'grey', gray:'grey', blue:'blue', navy:'blue',
    red:'red', maroon:'red', green:'green', purple:'purple', pink:'pink', gold:'gold',
    silver:'grey', orange:'orange'
  };

  const unique = (values) => [...new Set(values.filter(Boolean))];

  function legacyColourFamilies(data, titleN, typeN, tagSet) {
    const t = titleN;
    const isIpf = typeN.includes('ipf approved') || tagSet.has('ipf');

    // User-approved product/colourway exceptions first.
    if (t.includes('black and white')) return ['black'];
    if (t.includes('electric lemonade')) return ['blue'];
    if (t.includes('ivory rose')) return ['red'];
    if (t.includes('night light')) return ['blue'];
    if (t.includes('red dawn')) return ['red'];
    if (t.includes('shadow stone black')) return ['black'];
    if (t.includes('shadow stone grey')) return ['grey'];
    if (t.includes('shadow stone')) return ['black','grey'];
    if (t.includes('kinetic true-fit') && t.includes(' - shadow')) return ['black','grey'];
    if (t.includes('midnight')) return ['purple'];
    if (t.includes('monochrome')) return ['black'];
    if (t.includes('rwb navy')) return ['blue'];
    if (t.includes('rwb white')) return ['white'];
    if (/\brwb\b/.test(t)) return ['red','blue'];
    if (/\bgbr\b/.test(t)) {
      if (t.includes('meet shirt')) return ['white'];
      if (t.includes('zip up')) return ['blue'];
    }
    if (/\busa\b/.test(t)) return ['red','blue'];
    if (t.includes('aurora')) return ['blue'];
    if (t.includes('dusk')) return ['red'];
    if (t.includes('solaris')) return t.includes('bloom') ? ['red','white'] : ['red'];
    if (t.includes('stelacrosha tides')) return ['blue','grey','multi-pattern'];
    if (t.includes('tides white')) return ['white'];
    if (t.includes('tides blue')) return ['blue'];
    if (t.includes('tides')) return ['blue'];
    if (t.includes('pastel')) return ['purple','white','blue'];
    if (t.includes('domino white')) return ['white'];
    if (t.includes('domino black')) return ['black'];
    if (t.includes('domino')) return isIpf ? ['black','white'] : ['black'];
    if (t.includes('affirmation')) return ['black','multi-pattern'];
    if (t.includes('the void')) return ['black','blue','multi-pattern'];
    if (t.includes('loopo')) return ['multi-pattern'];
    if (t.includes('cosmic trip')) return ['blue'];
    if (t.includes('gold britannia')) return ['black','gold'];
    if (t === 'u bag') return ['black'];
    if (t.includes('crest true-fit')) return ['black'];
    if (t.includes('high vibes true-fit')) return ['black'];
    if (t.includes('roman warrior')) return ['black'];
    if (t.includes('dragon warrior')) return ['black'];
    if (t.includes('aztec warrior')) return ['black'];
    if (t.includes('geno - the bar is loaded')) return ['black'];
    if (t.includes('merino wool') && t.includes('beanie')) return ['black'];
    if (t.includes('dg barbell women') && t.includes('crop')) return ['black'];
    if (t.includes('americana headband')) return ['red','blue'];

    // Approved A7 named colourways.
    if (t.includes('flamingo')) return ['pink'];
    if (t.includes('roseite')) return ['pink'];
    if (t.includes('azul')) return ['blue'];
    if (t.includes('caeruleum')) return ['blue'];
    if (t.includes('ciel')) return ['blue'];
    if (t.includes('cyanite')) return ['blue'];
    if (t.includes('electric blue')) return ['blue'];
    if (t.includes('foggy navy')) return ['blue'];
    if (t.includes('gradient blue')) return ['blue'];
    if (t.includes('slate blue')) return ['blue'];
    if (t.includes('surf blue')) return ['blue'];
    if (t.includes('varsity blue')) return ['blue'];
    if (t.includes('navy')) return ['blue'];
    if (t.includes('gold standard')) return ['gold'];
    if (t.includes('alien')) return ['green'];
    if (t.includes('emerald forás') || t.includes('emerald foras')) return ['green'];
    if (t.includes('military')) return ['green'];
    if (t.includes('charcoal')) return ['grey'];
    if (t.includes('frost')) return ['grey'];
    if (t.includes('silver')) return ['grey'];
    if (t.includes('steel grey')) return ['grey'];
    if (t.includes('stelacrosha') && t.includes('grey')) return ['grey'];
    if (t.includes('blaze')) return ['orange'];
    if (t.includes('lavender')) return ['purple'];
    if (t.includes('violet tiger')) return ['purple'];
    if (t.includes('inferno')) return ['red'];
    if (t.includes('merlot')) return ['red'];
    if (t.includes('polar')) return ['white'];
    if (t.includes('the helix')) return ['white'];
    if (t.includes('classic black')) return ['black'];
    if (t.includes('black/ reflective') || t.includes('black/reflective')) return ['black'];
    if (t.includes('stealth')) return ['black'];
    if (t.includes('knight warrior')) return ['black'];

    // Explicit generic colour words in the product title are safe fallbacks.
    if (/\bblack\b/.test(t)) return ['black'];
    if (/\bwhite\b/.test(t)) return ['white'];
    if (/\bgrey\b/.test(t) || /\bgray\b/.test(t)) return ['grey'];
    if (/\bblue\b/.test(t)) return ['blue'];
    if (/\bred\b/.test(t)) return ['red'];
    if (/\bgreen\b/.test(t)) return ['green'];
    if (/\bpurple\b/.test(t)) return ['purple'];
    if (/\bpink\b/.test(t)) return ['pink'];
    if (/\bgold\b/.test(t)) return ['gold'];
    if (/\borange\b/.test(t)) return ['orange'];

    // Product metafield is intentionally preferred. This fallback only keeps the
    // approved catalogue usable while Shopify custom data is being populated.
    const fromTags = [];
    Object.entries(colourTagMap).forEach(([tag, family]) => {
      if (tagSet.has(tag)) fromTags.push(family);
    });
    return unique(fromTags);
  }
  const money = (raw) => {
    const n = Number(raw || 0);
    if (!Number.isFinite(n)) return 0;
    return n > 1000 ? n / 100 : n;
  };
  const priceBucket = (price) => price < 30 ? 'under30' : price <= 45 ? '30to45' : 'over45';

  function parseProduct(card) {
    const node = card.querySelector('[data-a7-product-json]');
    if (!node) return null;
    let data;
    try { data = JSON.parse(node.textContent); } catch (e) { return null; }

    const optionNames = Array.isArray(data.options) ? data.options : [];
    const title = data.title || '';
    const titleN = normalise(title);
    const typeN = normalise(data.type || '');
    const tags = (data.tags || []).map(normalise);
    const tagSet = new Set(tags);
    const metafieldColours = Array.isArray(data.colourFamilies) ? data.colourFamilies.map(normalise).filter(Boolean) : [];
    const colours = metafieldColours.length ? unique(metafieldColours) : legacyColourFamilies(data, titleN, typeN, tagSet);

    const variants = (data.variants || []).map(v => {
      const values = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter(Boolean);
      const opts = {};
      optionNames.forEach((name, i) => { opts[normalise(name)] = values[i]; });

      let stiffness = '';
      Object.keys(opts).forEach(name => {
        if (name.includes('stiffness')) stiffness = canonicalStiffness(opts[name]);
      });
      if (!stiffness) {
        if (titleN.includes('rigor mortis')) stiffness = 'Rigor Mortis';
        else if (titleN.includes('stiff cone')) stiffness = 'Stiff';
        else if (titleN.includes('cone knee sleeves')) stiffness = 'Flexi';
      }

      let size = '';
      let length = '';
      let thickness = '';
      Object.keys(opts).forEach(name => {
        if (name === 'size' || name.endsWith(' size')) size = canonicalSize(opts[name]);
        if (name === 'length' || name.endsWith(' length')) length = String(opts[name] || '').trim();
        if (name === 'thickness' || name.endsWith(' thickness')) thickness = String(opts[name] || '').trim();
      });
      if (!size && optionNames.length === 1 && normalise(optionNames[0]).includes('size')) {
        size = canonicalSize(values[0] || '');
      }

      return {
        id: v.id,
        available: Boolean(v.available),
        stiffness: canonicalStiffness(stiffness),
        size,
        length,
        thickness,
        price: money(v.price),
      };
    });

    const extraFamily = card.dataset.a7ExtraFamily || '';
    return { card, data, title, titleN, typeN, tags, tagSet, variants, colours, extraFamily, originalLinks: new Map(), _rangeCache: new Map(), _groupCache: new Map() };
  }

  function productGender(p) {
    if (Object.prototype.hasOwnProperty.call(p, '_genderCache')) return p._genderCache;
    const result = productGenderUncached(p);
    p._genderCache = result;
    return result;
  }

  function productGenderUncached(p) {
    const t = p.titleN;
    if (t.includes("women's") || t.includes('women’s') || t.includes('womens ')) return 'women';
    if (t.includes("men's") || t.includes('men’s') || t.includes('mens ')) return 'men';
    if (t.includes('unisex') || p.tagSet.has('unisex')) return 'unisex';
    const men = p.tagSet.has('mensall');
    const women = p.tagSet.has('womensall');
    if (men && !women) return 'men';
    if (women && !men) return 'women';
    if (men && women) return 'unisex';
    // The legacy/classic A7 singlet is a unisex product and has no gender in its title.
    if ((p.typeN.includes('singlet') || t.includes('singlet')) && !t.includes('luno') && !t.includes('rausch')) return 'unisex';
    return '';
  }

  function genderMatches(p, wanted) {
    return productGender(p) === wanted;
  }

  function rangeMatches(p, v, value) {
    const cache = p._rangeCache || (p._rangeCache = new Map());
    const cacheKey = `${v?.id || ''}|${value}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);
    const result = rangeMatchesUncached(p, v, value);
    cache.set(cacheKey, result);
    return result;
  }

  function rangeMatchesUncached(p, v, value) {
    const t = p.titleN;
    const type = p.typeN;
    const isKnee = type.includes('knee sleeve') || t.includes('knee sleeve');
    const isSinglet = type.includes('singlet') || t.includes('singlet');
    const isBelt = type.includes('belt') || t.includes('belt') || t.includes('buckle');
    const isWrist = type.includes('wrist wrap') || t.includes('wrist wrap');
    const isMeet = type.includes('meet tee') || t.includes('meet shirt');
    const isSock = type === 'socks' || type.includes('sock');
    const isUnderwear = type.includes('underwear') || type.includes('boxer') || t.includes('boxer');
    const isOrganicCotton = type.includes('organic cotton') || t.includes('organic cotton') || p.tagSet.has('organic-cotton') || p.tagSet.has('oc');
    // Some legacy products have a Shirt product type even though the customer-facing
    // product is actually outerwear (for example a Bar Grip Hoodie). Treat obvious
    // outerwear names as authoritative so they cannot leak into Shirt buckets.
    const isOuterwearLike = [
      'hoodie','sweatshirt','jacket','quarter zip','zip up','zip-up',
      'crew neck','crewneck','flxcrw'
    ].some(term => t.includes(term));
    const handle = normalise(p.data?.handle || '');
    const isCrewLike = t.includes('crew neck') || t.includes('crewneck') || t.includes('flxcrw') || (type.includes('outerwear') && t.includes('crew'));
    const isPositiveBarGripCrew = isCrewLike && t.includes('bar grip') && !t.includes('non bar grip');
    const isLegacyWomensNbgTee = ['2024-april-fools-womens-meat-shirt','2023p-womens-shirt-slide-stealth-wave-edc','deflection-womens-shirt-the-helix'].includes(handle);
    const inStrongman = p.tagSet.has('strongman');
    const inStrongwoman = p.tagSet.has('strongwoman');
    const explicitWomen = t.includes("women's") || t.includes('women’s') || t.includes('womens ');
    // Important: "women's" contains the substring "men's". Without this
    // guard, almost every explicitly Women's product was incorrectly treated as
    // Men's and excluded from the Women's taxonomy / QA audit.
    const explicitMen = !explicitWomen && (t.includes("men's") || t.includes('men’s') || t.includes('mens '));
    const inMensCatalog = !explicitWomen && (genderMatches(p, 'men') || p.tagSet.has('mensall') || p.tagSet.has('nonbargripmens') || p.tagSet.has('mensouterwear') || p.tagSet.has('mensjoggers'));
    const inWomensCatalog = !explicitMen && (genderMatches(p, 'women') || p.tagSet.has('womensall') || p.tagSet.has('nonbargripwomens') || p.tagSet.has('womensouterwear') || p.tagSet.has('womensjoggers'));
    const isIpfProduct = p.tagSet.has('ipf') || p.tagSet.has('ipfnew') || type.includes('ipf approved') || t.includes('ipf approved');
    const isAccessoryBag = type === 'bag' || type.includes('gym bag') || (t.includes('bag') && !t.includes('keychain'));
    const isAccessoryBottles = type.includes('bottles & shakers') || type.includes('bottle') || type.includes('shaker') || p.tagSet.has('bottles-shakers');
    const isAccessoryCompression = type.includes('compression pants') || t.includes('compression pants');
    const isAccessoryDogToy = type.includes('dog toy') || t.includes('dog toy');
    const isAccessoryHat = type === 'hats' || type.includes('hat') || t.includes('beanie') || t.includes('dad hat');
    const isAccessoryHeadband = type.includes('headband') || t.includes('headband');
    const isAccessoryStrap = type === 'straps' || type.includes('lifting strap') || t.includes('pull strap') || t.includes('lifting strap');
    const isAccessoryElbowSleeve = type.includes('elbow sleeve') || t.includes('elbow sleeve');
    const isAccessoryStrongmanSleeve = type.includes('strongman sleeve') || isAccessoryElbowSleeve || t.includes('strongman knee sleeve');
    const isAccessorySlipper = type.includes('slipper') || t.includes('slipper');

    switch (value) {
      case 'knee-flexi': return isKnee && v.stiffness === 'Flexi';
      case 'knee-stiff': return isKnee && v.stiffness === 'Stiff';
      case 'knee-rigor': return isKnee && v.stiffness === 'Rigor Mortis';

      case 'singlet-luno': return isSinglet && t.includes('luno') && !t.includes('long leg');
      case 'singlet-rausch': return isSinglet && t.includes('rausch') && !t.includes('long leg');
      case 'singlet-rausch-long': return isSinglet && t.includes('rausch') && t.includes('long leg');
      case 'singlet-classic': return isSinglet && !t.includes('luno') && !t.includes('rausch');

      case 'belt-lever': return isBelt && !t.includes('prong') && (t.includes('lever') || t.includes('buckle'));
      case 'belt-prong': return isBelt && t.includes('prong');
      case 'belt-buckle': return isBelt && t.includes('buckle');

      case 'wrist-zebra-flexi': return isWrist && t.includes('zebra') && v.stiffness === 'Flexi';
      case 'wrist-zebra-mids': return isWrist && t.includes('zebra') && v.stiffness === 'Mids';
      case 'wrist-zebra-stiff': return isWrist && t.includes('zebra') && v.stiffness === 'Stiff';
      case 'wrist-zebra-rigor': return isWrist && t.includes('zebra') && v.stiffness === 'Rigor Mortis';
      case 'wrist-a7': return isWrist && !t.includes('zebra');

      case 'mens-nbg-overfit': return inMensCatalog && type.includes('non bar grip') && (t.includes('over-fit') || t.includes('cut off'));
      case 'mens-nbg-truefit': return inMensCatalog && type === 'non bar grip shirt' && !isOuterwearLike && t.includes('true-fit') && !t.includes('over-fit') && !t.includes('cut off') && !t.includes('polo') && !t.includes('meet shirt');
      case 'mens-nbg-shirts': return inMensCatalog && type === 'non bar grip shirt' && !isOuterwearLike && !t.includes('true-fit') && !t.includes('over-fit') && !t.includes('cut off') && !t.includes('polo') && !t.includes('meet shirt') && !t.includes('long sleeve');
      case 'mens-nbg-meet': return inMensCatalog && isMeet;
      case 'mens-nbg-polo': return inMensCatalog && t.includes('polo');
      case 'mens-nbg-stringer': return inMensCatalog && (type === 'stringer' || t.includes('stringer'));
      case 'mens-nbg-long-sleeve': return inMensCatalog && (type === 'long sleeve shirt' || t.includes('long sleeve'));

      case 'socks-deadlift': return isSock && t.includes('deadlift');
      case 'socks-crew': return isSock && t.includes('crew');
      case 'underwear-boxers': return isUnderwear && (type.includes('organic cotton boxer') || t.includes('organic cotton') && t.includes('boxer'));

      case 'meet-mens': return isMeet && genderMatches(p, 'men');
      case 'meet-womens': return isMeet && genderMatches(p, 'women');

      case 'mens-bg-shirt': return inMensCatalog && type === 'bar grip shirt' && !inStrongman && !isOuterwearLike;
      case 'mens-bg-tank': return inMensCatalog && type === 'bar grip tank';

      case 'mens-shorts-1z': return (type.includes('360go') || t.includes('360go')) && (type.includes('1z') || t.includes('1z')) && (type.includes('short') || t.includes('short'));
      case 'mens-shorts-kwd': return (type.includes('360go') || t.includes('360go')) && (type.includes('kwd') || t.includes('kwd')) && (type.includes('short') || t.includes('short')) && !type.includes('1z') && !t.includes('1z');
      case 'mens-shorts-regular': return (type.includes('360go') || t.includes('360go')) && (type.includes('short') || t.includes('short')) && !type.includes('kwd') && !t.includes('kwd') && !type.includes('1z') && !t.includes('1z');
      case 'mens-shorts-mvn': return isOrganicCotton && (type.includes('short') || t.includes('short'));
      case 'mens-shorts-squat': return type.includes('squat shorts') || t.includes('squat shorts');

      case 'mens-hoodie-bargrip': return inMensCatalog && (t.includes('hoodie') || isCrewLike) && t.includes('bar grip') && !t.includes('non bar grip') && !t.includes('organic cotton');
      case 'mens-hoodie-nonbar': return inMensCatalog && t.includes('hoodie') && t.includes('non bar grip') && !t.includes('organic cotton');
      case 'mens-hoodie-organic': return inMensCatalog && t.includes('organic cotton') && t.includes('hoodie');
      case 'mens-hoodie-zip': return inMensCatalog && t.includes('jacket') && (t.includes('zip up') || t.includes('zip-up') || t.includes('quarter zip'));
      case 'mens-hoodie-crew': return inMensCatalog && isCrewLike && !isPositiveBarGripCrew && !t.includes('jogger');

      case 'mens-bottoms-defy': return (type.includes('defy') || t.includes('defy')) && (type.includes('jogger') || t.includes('jogger'));
      case 'mens-bottoms-organic': return isOrganicCotton && (type.includes('jogger') || t.includes('jogger'));
      case 'mens-bottoms-cobra': return (type.includes('cobra') || t.includes('cobra')) && (type.includes('jogger') || t.includes('jogger'));
      case 'mens-bottoms-compression': return (type.includes('compression') || t.includes('compression')) && (type.includes('pant') || t.includes('pant'));
      case 'mens-bottoms-script': return (type.includes('script') || t.includes('script')) && (type.includes('jogger') || t.includes('jogger'));

      case 'mens-organic-shirts': return inMensCatalog && isOrganicCotton && (type.includes('shirt') || t.includes('shirt')) && !t.includes('hoodie');
      case 'mens-organic-hoodies': return inMensCatalog && isOrganicCotton && t.includes('hoodie');
      case 'mens-organic-shorts': return inMensCatalog && isOrganicCotton && (type.includes('short') || t.includes('short'));
      case 'mens-organic-joggers': return inMensCatalog && isOrganicCotton && (type.includes('jogger') || t.includes('jogger'));
      case 'mens-organic-underwear': return inMensCatalog && isOrganicCotton && isUnderwear;

      case 'mens-strongman-bargrip': return inStrongman && !isOuterwearLike && (type.includes('bar grip shirt') || (t.includes('bar grip') && t.includes('shirt')));
      case 'mens-strongman-belts': return isBelt && (p.tagSet.has('ipf') || p.tagSet.has('strongman') || type.includes('ipf approved') || t.includes('ipf approved'));
      case 'mens-strongman-knee': return isKnee && !type.includes('strongman sleeve') && !t.includes('strongman knee sleeve') && (p.tagSet.has('ipf') || p.tagSet.has('ipfnew') || type.includes('ipf approved') || t.includes('ipf approved'));
      case 'mens-strongman-sleeves': return inStrongman && (type.includes('strongman sleeve') || t.includes('strongman knee sleeve') || t.includes('elbow sleeve') || type.includes('elbow sleeve'));

      case 'womens-bg-shirt': return inWomensCatalog && type === 'bar grip shirt' && !t.includes('non bar grip') && !inStrongwoman && !isOuterwearLike;
      case 'womens-bg-crop': return inWomensCatalog && (type === 'bar grip crop' || (t.includes('bar grip') && t.includes('crop'))) && !t.includes('non bar grip');

      case 'womens-nbg-overfit': return inWomensCatalog && (type.includes('non bar grip') || t.includes('non bar grip')) && (t.includes('over-fit') || t.includes('cut off'));
      case 'womens-nbg-truefit': return inWomensCatalog && (type === 'non bar grip shirt' || t.includes('non bar grip')) && !isOuterwearLike && t.includes('true-fit') && !t.includes('crop') && !t.includes('over-fit') && !t.includes('cut off') && !t.includes('meet shirt');
      case 'womens-nbg-meet': return inWomensCatalog && isMeet;
      case 'womens-nbg-crop': return inWomensCatalog && (type === 'non bar grip crop' || (t.includes('non bar grip') && t.includes('crop')) || isLegacyWomensNbgTee);
      case 'womens-nbg-shirts': return inWomensCatalog && isLegacyWomensNbgTee;
      case 'womens-nbg-long-sleeve': return inWomensCatalog && (type === 'long sleeve shirt' || t.includes('long sleeve'));

      case 'womens-sports-crop-xo': return handle === 'xo-hew-womens-sports-bra-night' || (inWomensCatalog && t.includes('xo hew') && (t.includes('sports crop') || type.includes('sports bra')));

      // V41 universal homepage/mixed-collection drill-down. The top-level
      // Men's / Women's / IPF / Accessories cards now expose the same useful
      // product-family structure we built elsewhere, rather than opening into
      // an empty body with only Size/Colour.
      case 'universal-mens-tops': return ['mens-bg-shirt','mens-bg-tank','mens-nbg-overfit','mens-nbg-truefit','mens-nbg-meet','mens-nbg-polo','mens-nbg-shirts','mens-nbg-stringer','mens-nbg-long-sleeve'].some(x => rangeMatches(p, v, x));
      case 'universal-mens-shorts': return ['mens-shorts-kwd','mens-shorts-1z','mens-shorts-regular','mens-shorts-mvn'].some(x => rangeMatches(p, v, x));
      case 'universal-mens-hoodies': return ['mens-hoodie-bargrip','mens-hoodie-nonbar','mens-hoodie-organic','mens-hoodie-zip','mens-hoodie-crew'].some(x => rangeMatches(p, v, x));
      case 'universal-mens-bottoms': return ['mens-bottoms-defy','mens-bottoms-organic','mens-bottoms-cobra','mens-bottoms-compression','mens-bottoms-script'].some(x => rangeMatches(p, v, x));
      case 'universal-mens-strongman': return ['mens-strongman-bargrip','mens-strongman-belts','mens-strongman-knee','mens-strongman-sleeves'].some(x => rangeMatches(p, v, x));
      case 'universal-mens-organic': return ['mens-organic-shirts','mens-organic-hoodies','mens-organic-shorts','mens-organic-joggers','mens-organic-underwear'].some(x => rangeMatches(p, v, x));
      case 'universal-mens-underwear': return rangeMatches(p, v, 'underwear-boxers');

      case 'universal-womens-tops': return ['womens-bg-shirt','womens-bg-crop','womens-nbg-overfit','womens-nbg-truefit','womens-nbg-meet','womens-nbg-crop','womens-nbg-long-sleeve'].some(x => rangeMatches(p, v, x));
      case 'universal-womens-sports-crops': return rangeMatches(p, v, 'womens-sports-crop-xo');
      case 'universal-womens-shorts': return ['womens-shorts-kwd','womens-shorts-1z','womens-shorts-regular','womens-shorts-organic'].some(x => rangeMatches(p, v, x));
      case 'universal-womens-hoodies': return ['womens-hoodie-bargrip','womens-hoodie-nonbar','womens-hoodie-organic','womens-hoodie-zip','womens-hoodie-crew'].some(x => rangeMatches(p, v, x));
      case 'universal-womens-bottoms': return ['womens-bottoms-defy','womens-bottoms-organic','womens-bottoms-cobra','womens-bottoms-flexure','womens-bottoms-script','womens-bottoms-leggings'].some(x => rangeMatches(p, v, x));
      case 'universal-womens-strongwoman': return ['womens-strongwoman-bargrip','womens-strongwoman-belts','womens-strongwoman-knee','womens-strongwoman-sleeves'].some(x => rangeMatches(p, v, x));
      case 'universal-womens-organic': return ['womens-organic-shirts','womens-organic-hoodies','womens-organic-shorts','womens-organic-joggers'].some(x => rangeMatches(p, v, x));

      case 'universal-ipf-knee': return isIpfProduct && isKnee;
      case 'universal-ipf-singlets': return isIpfProduct && isSinglet;
      case 'universal-ipf-belts': return isIpfProduct && isBelt;
      case 'universal-ipf-wrist': return isIpfProduct && isWrist;
      case 'universal-ipf-meet': return isIpfProduct && isMeet;
      case 'universal-ipf-socks': return isIpfProduct && isSock;
      case 'universal-ipf-underwear': return isIpfProduct && isUnderwear;

      case 'universal-accessories-bag': return isAccessoryBag;
      case 'universal-accessories-bottles': return isAccessoryBottles;
      case 'universal-accessories-compression': return isAccessoryCompression;
      case 'universal-accessories-dog-toys': return isAccessoryDogToy;
      case 'universal-accessories-hats': return isAccessoryHat && !isAccessoryHeadband;
      case 'universal-accessories-headbands': return isAccessoryHeadband;
      case 'universal-accessories-headwear': return (isAccessoryHat && !isAccessoryHeadband) || isAccessoryHeadband;
      case 'universal-accessories-socks': return isSock;
      case 'universal-accessories-slippers': return isAccessorySlipper;
      case 'universal-accessories-straps': return isAccessoryStrap;

      // Accessories hub child ranges. V45 had the parent groups and product
      // classification, but these child cases were missing from rangeMatches(),
      // so Headwear was hidden and Pull Straps could not surface as Lifting Straps.
      case 'accessories-hats': return isAccessoryHat && !isAccessoryHeadband;
      case 'accessories-headbands': return isAccessoryHeadband;
      case 'accessories-elbow-sleeves': return isAccessoryElbowSleeve;
      case 'accessories-straps': return isAccessoryStrap;
      case 'accessories-slippers': return isAccessorySlipper;
      case 'universal-accessories-lifting': return isAccessoryElbowSleeve || isAccessoryStrap;
      case 'universal-accessories-strongman-sleeves': return isAccessoryStrongmanSleeve;
      case 'universal-accessories-underwear': return isUnderwear;

      case 'womens-shorts-1z': return inWomensCatalog && (type.includes('360go') || t.includes('360go')) && (type.includes('1z') || t.includes('1z')) && (type.includes('short') || t.includes('short'));
      case 'womens-shorts-kwd': return inWomensCatalog && (type.includes('360go') || t.includes('360go')) && (type.includes('kwd') || t.includes('kwd')) && (type.includes('short') || t.includes('short')) && !type.includes('1z') && !t.includes('1z');
      case 'womens-shorts-regular': return inWomensCatalog && (type.includes('360go') || t.includes('360go')) && (type.includes('short') || t.includes('short')) && !type.includes('kwd') && !t.includes('kwd') && !type.includes('1z') && !t.includes('1z');
      case 'womens-shorts-organic': return inWomensCatalog && isOrganicCotton && (type.includes('short') || t.includes('short'));

      case 'womens-hoodie-bargrip': return inWomensCatalog && (t.includes('hoodie') || isCrewLike) && t.includes('bar grip') && !t.includes('non bar grip') && !t.includes('organic cotton');
      case 'womens-hoodie-nonbar': return inWomensCatalog && t.includes('hoodie') && t.includes('non bar grip') && !t.includes('organic cotton');
      case 'womens-hoodie-organic': return inWomensCatalog && t.includes('organic cotton') && t.includes('hoodie');
      case 'womens-hoodie-zip': return inWomensCatalog && (t.includes('zip up') || t.includes('zip-up') || t.includes('quarter zip'));
      case 'womens-hoodie-crew': return inWomensCatalog && isCrewLike && !isPositiveBarGripCrew && !t.includes('jogger');

      case 'womens-bottoms-defy': return inWomensCatalog && (type.includes('defy') || t.includes('defy')) && (type.includes('jogger') || t.includes('jogger'));
      case 'womens-bottoms-organic': return inWomensCatalog && isOrganicCotton && (type.includes('jogger') || t.includes('jogger'));
      case 'womens-bottoms-cobra': return inWomensCatalog && (type.includes('cobra') || t.includes('cobra')) && (type.includes('jogger') || t.includes('jogger'));
      case 'womens-bottoms-flexure': return inWomensCatalog && (type.includes('flexure') || t.includes('flexure')) && (type.includes('jogger') || t.includes('jogger'));
      case 'womens-bottoms-script': return inWomensCatalog && (type.includes('script') || t.includes('script')) && (type.includes('jogger') || t.includes('jogger'));
      case 'womens-bottoms-leggings': return inWomensCatalog && (type.includes("women's leggings") || type.includes('womens leggings') || t.includes("women's leggings") || t.includes('womens leggings') || type === 'leggings');

      case 'womens-organic-shirts': return inWomensCatalog && isOrganicCotton && (type.includes('shirt') || t.includes('shirt')) && !t.includes('hoodie');
      case 'womens-organic-hoodies': return inWomensCatalog && isOrganicCotton && t.includes('hoodie');
      case 'womens-organic-shorts': return inWomensCatalog && isOrganicCotton && (type.includes('short') || t.includes('short'));
      case 'womens-organic-joggers': return inWomensCatalog && isOrganicCotton && (type.includes('jogger') || t.includes('jogger'));

      case 'womens-strongwoman-bargrip': return inStrongwoman && !isOuterwearLike && (type.includes('bar grip shirt') || (t.includes('bar grip') && t.includes('shirt')));
      case 'womens-strongwoman-belts': return isBelt && (p.tagSet.has('strongwoman') || p.tagSet.has('ipf') || type.includes('ipf approved') || t.includes('ipf approved'));
      case 'womens-strongwoman-knee': return isKnee && !type.includes('strongman sleeve') && !t.includes('strongman knee sleeve') && (p.tagSet.has('strongwoman') || p.tagSet.has('ipf') || p.tagSet.has('ipfnew') || type.includes('ipf approved') || t.includes('ipf approved'));
      case 'womens-strongwoman-sleeves': return inStrongwoman && (type.includes('strongman sleeve') || t.includes('strongman knee sleeve') || t.includes('elbow sleeve') || type.includes('elbow sleeve'));
      default: return false;
    }
  }

  function init(root) {
    if (root.dataset[READY_FLAG]) return;
    root.dataset[READY_FLAG] = '1';

    const panel = root.querySelector('[data-a7-filter-panel]');
    // Multiple completeness sources can contain the same Shopify product (for
    // example an IPF belt can also sit in the Strongman collection). Keep only
    // the first card for each handle so counts and results stay product-unique.
    const seenProductHandles = new Set();
    const cards = [...document.querySelectorAll('[data-a7-filter-product]')]
      .map(parseProduct)
      .filter(Boolean)
      .filter(p => {
        const handle = normalise(p.data?.handle || '');
        if (!handle) return true;
        if (seenProductHandles.has(handle)) {
          p.card.hidden = true;
          p.card.dataset.a7DuplicateProduct = 'true';
          return false;
        }
        seenProductHandles.add(handle);
        return true;
      });
    if (!panel || !cards.length) return;

    cards.forEach(p => {
      p.card.querySelectorAll('[data-product-card-link]').forEach(link => p.originalLinks.set(link, link.getAttribute('href')));
    });

    const inputs = [...root.querySelectorAll('[data-a7-filter-input]')];
    const includeSoldOut = root.querySelector('[data-a7-filter-include-sold-out]');
    const counts = [...root.querySelectorAll('[data-a7-option-count]')];
    const chips = root.querySelector('[data-a7-active-chips]');
    const noResultsHost = document.querySelector('#template--collection');

    const state = { range:new Set(), gender:new Set(), size:new Set(), length:new Set(), thickness:new Set(), colour:new Set(), price:new Set(), includeSoldOut:false };


    function resolveCollectionGroups() {
      const handle = normalise(root.dataset.a7CollectionHandle || '');
      const title = normalise(root.dataset.a7CollectionTitle || '');
      const raw = normalise(title + ' ' + handle).replaceAll('-', ' ');
      const isWomen = raw.includes("women's") || raw.includes('womens') || raw.includes('women ');
      const isMen = !isWomen && (raw.includes("men's") || raw.includes('men’s') || raw.includes('mens') || raw.includes('men '));
      const isMensMenuCollection = root.dataset.a7MensMenuCollection === 'true';
      const isWomensMenuCollection = root.dataset.a7WomensMenuCollection === 'true';
      const isAccessoriesMenuCollection = root.dataset.a7AccessoriesMenuCollection === 'true';
      const mensHubGroups = ['mens-tops-all','mens-shorts-all','mens-hoodies-all','mens-bottoms-all','mens-strongman-all','mens-organic-all','mens-underwear-all','mens-unclassified'];
      const womensHubGroups = ['womens-tops-all','womens-shorts-all','womens-hoodies-all','womens-bottoms-all','womens-strongwoman-all','womens-organic-all','womens-sports-crops-all','womens-unclassified'];
      const accessoriesHubGroups = ['accessories-bag','accessories-bottles','accessories-compression','accessories-dog-toys','accessories-headwear-all','accessories-socks','accessories-slippers','accessories-lifting-all','accessories-unclassified'];
      const universalGroups = ['universal-mens','universal-womens','universal-ipf','universal-accessories','universal-unclassified'];
      // Launch/collection pages linked beneath Men's need a broader adaptive taxonomy
      // than Men's All. In particular, IPF equipment should keep its customer-facing
      // IPF labels instead of being absorbed into Strongman just because products also
      // carry a Strongman tag.
      const mensLaunchGroups = [
        'mens-tops-all','mens-shorts-all','mens-hoodies-all','mens-bottoms-all',
        'mens-organic-all','mens-underwear-all',
        'knee','singlets','belts','wrist','meet','socks',
        'mens-strongman-launch','mens-unclassified'
      ];
      const womensLaunchGroups = [
        'womens-tops-all','womens-shorts-all','womens-hoodies-all','womens-bottoms-all',
        'womens-organic-all','womens-sports-crops-all',
        'knee','singlets','belts','wrist','meet','socks',
        'womens-strongwoman-launch','womens-unclassified'
      ];

      if (handle === 'accessories' || title === 'accessories') {
        return new Set(accessoriesHubGroups);
      }

      if (handle === 'womensall' || title === "women's all" || title === 'women’s all' || title === 'womens all' || title === "women's" || title === 'women’s') {
        return new Set(womensHubGroups);
      }
      if (handle === 'mens' || title === "men's" || title === 'men’s' || title === 'mens' || title === "men's all" || title === 'men’s all' || title === 'mens all') {
        return new Set(mensHubGroups);
      }
      // Explicit Women's PRODUCT-menu routing. These exact collection routes
      // take priority over adaptive launch routing, while new launch collections
      // linked under Women's inherit the broader contextual taxonomy below.
      const explicitWomensBarGrip = (
        title.includes("women's bar grip") || title.includes('women’s bar grip') || title.includes('womens bar grip') ||
        handle === 'womens' || handle.includes('womens-bar-grip') || handle.includes('womens_bar_grip')
      ) && !raw.includes('non bar grip') && !handle.includes('non-bar-grip');
      const explicitWomensNonBarGrip =
        title.includes("women's non bar grip") || title.includes('women’s non bar grip') || title.includes('womens non bar grip') ||
        handle === 'womens-non-bar-grips' || handle.includes('womens-non-bar-grip') || handle.includes('womens_non_bar_grip');

      if (explicitWomensNonBarGrip) return new Set(['womens-nbg']);
      if (explicitWomensBarGrip) return new Set(['womens-bg']);
      if (handle === 'womens-sports-crops' || title.includes("women's sports crop") || title.includes('women’s sports crop') || title.includes('womens sports crop')) return new Set(['womens-sports-crops']);
      if (handle === 'womens-shorts' || title.includes("women's shorts") || title.includes('women’s shorts') || title.includes('womens shorts')) return new Set(['womens-shorts']);
      if (handle === 'womens-joggers' || title.includes("women's bottoms") || title.includes('women’s bottoms') || title.includes('womens bottoms')) return new Set(['womens-bottoms']);
      if (handle === 'womens-outerwear' || title.includes("women's outerwear") || title.includes('women’s outerwear') || title.includes('womens outerwear')) return new Set(['womens-hoodies']);
      if (handle === 'strongwoman' || title === 'strongwoman') return new Set(['womens-strongwoman']);
      if ((handle === 'organic-cotton-womens' || (isWomensMenuCollection && raw.includes('organic cotton')))) return new Set(['womens-organic']);

      // Explicit Men's PRODUCT-menu routing. These exact routes must win over the
      // broader launch-page logic below. The important distinction is that a launch
      // collection such as "10 Years of Bar Grip" may contain the words Bar Grip,
      // but it is NOT the dedicated "Men's Bar Grips" product collection.
      const explicitMensBarGrip = (
        title.includes("men's bar grip") || title.includes('men’s bar grip') || title.includes('mens bar grip') ||
        handle.includes('mens-bar-grip') || handle.includes('mens_bar_grip')
      ) && !raw.includes('non bar grip') && !handle.includes('non-bar-grip');
      const explicitMensNonBarGrip =
        title.includes("men's non bar grip") || title.includes('men’s non bar grip') || title.includes('mens non bar grip') ||
        handle.includes('mens-non-bar-grip') || handle.includes('mens_non_bar_grip');

      if (explicitMensNonBarGrip) return new Set(['mens-nbg']);
      if (explicitMensBarGrip) return new Set(['mens-bg']);
      if ((title.includes("men's shorts") || title.includes('men’s shorts') || title.includes('mens shorts')) || handle.includes('mens-short')) return new Set(['mens-shorts']);
      if ((title.includes("men's bottoms") || title.includes('men’s bottoms') || title.includes('mens bottoms')) || handle.includes('mens-bottom')) return new Set(['mens-bottoms']);
      if (handle === 'mens-outerwear' || title.includes("men's outerwear") || title.includes('men’s outerwear') || title.includes('mens outerwear') || title.includes('hoodie') || title.includes('sweatshirt') || handle.includes('hoodie') || handle.includes('sweatshirt')) return new Set(['mens-hoodies']);
      if (title === 'strongman' || handle === 'strongman' || handle === 'strongman-1') return new Set(['mens-strongman']);
      // Organic Cotton intentionally keeps its dedicated product-type breakdown.
      if (title === 'organic cotton' || handle === 'organic-cotton') return new Set(['mens-organic']);

      // V40: explicit Accessories submenu routing. We keep the navigation-tree
      // detection below for future additions, but the current live Accessories
      // destinations are also recognised by handle. This prevents a narrow
      // Accessories collection from falling through to a generic/unrelated route
      // if its menu metadata or title is unusual.
      if (handle === 'headwear' || handle.includes('headwear')) return new Set(['accessories-headwear-all']);
      if (handle === 'lifting-accessories' || raw.includes('lifting accessories')) return new Set(['accessories-lifting-all']);

      const explicitAccessoryDirect = (
        handle === 'bag' ||
        handle === 'lifting-straps' || handle.includes('lifting-strap') ||
        handle === 'gym-banners' || handle.includes('gym-banner') ||
        handle === 'bottles-shakers-1' || handle.includes('bottles-shakers') ||
        handle === 'stickers-decals-1' || handle.includes('stickers-decals') || handle.includes('sticker') ||
        handle === 'dog-toys' || handle.includes('dog-toy') ||
        handle === 'keychains' || handle === 'keychain' || handle.includes('keychain') ||
        handle === 'underwear' ||
        handle === 'elbow-sleeves' || handle.includes('elbow-sleeve') ||
        handle === 'strongman-sleeves' || handle.includes('strongman-sleeve')
      );
      const explicitAccessorySocks = (
        handle === 'socks' || handle === 'accessories-socks' ||
        (handle.includes('sock') && !handle.includes('knee-sock'))
      );
      if (handle === 'footwear') return new Set(['accessories-socks','accessories-slippers']);
      if (explicitAccessorySocks) return new Set(['accessories-socks']);
      if (explicitAccessoryDirect) return new Set(['accessories-direct']);

      // Accessories menu routing. Any future collection added beneath Accessories
      // inherits the A7 custom Size/Colour/Price/Availability/Sort controls without
      // needing a theme-code update. Footwear has a dedicated Socks/Slippers structure.
      if (isAccessoriesMenuCollection) {
        if (handle === 'footwear') return new Set(['accessories-socks','accessories-slippers']);
        if (raw.includes('headwear')) return new Set(['accessories-headwear-all']);
        if (raw.includes('lifting accessories')) return new Set(['accessories-lifting-all']);
        if (raw.includes('sock')) return new Set(['accessories-socks']);
        return new Set(['accessories-direct']);
      }

      // Women's launch/collection pages mirror the Men's adaptive routing. Any
      // collection linked beneath Women's can expose the relevant product families,
      // including IPF families, without needing a future theme-code edit.
      if (isWomensMenuCollection || handle.endsWith('-womens') || handle.includes('-womens-')) {
        return new Set(womensLaunchGroups);
      }

      // V34: once a collection is known to be a launch/collection link beneath Men's,
      // use the adaptive Men's taxonomy BEFORE generic keyword routing. This prevents
      // names such as "10 Years of Bar Grip" from collapsing to the dedicated Bar
      // Grip filter and incorrectly making a hoodie look like it belongs under shirts.
      if (isMensMenuCollection || (!isWomen && (handle.endsWith('-mens') || handle.includes('-mens-')))) {
        return new Set(mensLaunchGroups);
      }

      // Fallback routing for mapped collections that are not currently linked beneath
      // Men's navigation.
      if (raw.includes('knee sleeve')) return new Set(['knee']);
      if (raw.includes('singlet')) return new Set(['singlets']);
      if (raw.includes('belt')) return new Set(['belts']);
      if (raw.includes('wrist wrap')) return new Set(['wrist']);
      if (raw.includes('non bar grip')) {
        if (isWomen) return new Set(['womens-nbg']);
        if (isMen) return new Set(['mens-nbg']);
      }
      if (raw.includes('bar grip')) {
        if (isWomen) return new Set(['womens-bg']);
        if (isMen) return new Set(['mens-bg']);
      }
      if (raw.includes('meet tee') || raw.includes('meet shirt')) return new Set(['meet']);
      if (raw.includes('strongman sleeve') || raw.includes('elbow sleeve')) return new Set(['accessories-direct']);
      if (raw.includes('gym bag') || raw.includes('lifting strap') || raw.includes('headwear') || raw.includes('gym banner') || raw.includes('bottle') || raw.includes('shaker') || raw.includes('dog toy') || raw.includes('keychain') || raw.includes('sticker')) return new Set(['accessories-direct']);
      if (raw.includes('sock')) return new Set(['socks']);
      if (raw.includes('sports crop') && isWomen) return new Set(['womens-sports-crops']);
      if ((raw.includes('bottom') || raw.includes('jogger') || raw.includes('legging')) && isWomen) return new Set(['womens-bottoms']);
      if ((raw.includes('hoodie') || raw.includes('sweatshirt') || raw.includes('outerwear')) && isWomen) return new Set(['womens-hoodies']);
      if (raw.includes('strongwoman')) return new Set(['womens-strongwoman']);
      if (raw.includes('short')) return new Set([isWomen ? 'womens-shorts' : 'mens-shorts']);
      if (raw.includes('underwear')) return new Set(['underwear']);
      if (raw.includes('ipf approved') || raw.includes('ipf')) return new Set(['knee','singlets','belts','wrist','meet','socks','ipf-underwear','ipf-unclassified']);

      // V39: any otherwise-unmapped collection (including homepage-only launch
      // collections such as GBR / New Items) stays inside the A7 filter system.
      return new Set(universalGroups);
    }

    const collectionGroups = resolveCollectionGroups();
    const isIpfHub = Boolean(collectionGroups && collectionGroups.size > 1 && collectionGroups.has('knee') && collectionGroups.has('singlets'));
    const isMensHub = Boolean(collectionGroups && collectionGroups.size > 1 && collectionGroups.has('mens-tops-all') && collectionGroups.has('mens-bottoms-all'));
    const isWomensHub = Boolean(collectionGroups && collectionGroups.size > 1 && collectionGroups.has('womens-tops-all') && collectionGroups.has('womens-bottoms-all'));
    const isAccessoriesHub = Boolean(collectionGroups && collectionGroups.has('accessories-unclassified') && collectionGroups.has('accessories-socks'));
    const isUniversalHub = Boolean(collectionGroups && collectionGroups.has('universal-unclassified') && collectionGroups.has('universal-mens'));
    const isContextHub = isIpfHub || isMensHub || isWomensHub || isAccessoriesHub || isUniversalHub;
    let activeFamily = '';

    function productInGroup(p, key) {
      const cache = p._groupCache || (p._groupCache = new Map());
      if (cache.has(key)) return cache.get(key);
      const result = productInGroupUncached(p, key);
      cache.set(key, result);
      return result;
    }

    function productInGroupUncached(p, key) {
      const t = p.titleN;
      const type = p.typeN;
      const isKnee = type.includes('knee sleeve') || t.includes('knee sleeve');
      const isSinglet = type.includes('singlet') || t.includes('singlet');
      const isBelt = type.includes('belt') || t.includes('belt') || t.includes('buckle');
      const isWrist = type.includes('wrist wrap') || t.includes('wrist wrap');
      const isMeet = type.includes('meet tee') || t.includes('meet shirt');
      const isSock = type === 'socks' || type.includes('sock');
      const isUnderwear = type.includes('underwear') || type.includes('boxer') || t.includes('boxer');
      const isGiftCard = p.data?.handle === 'gift-card' || type === 'gift card';
      if (key === 'knee') return isKnee;
      if (key === 'singlets') return isSinglet;
      if (key === 'belts') return isBelt;
      if (key === 'wrist') return isWrist;
      if (key === 'meet') return isMeet;
      if (key === 'socks') return isSock;
      if (key === 'underwear') return isUnderwear;
      if (key === 'ipf-underwear') return isUnderwear;
      if (key === 'ipf-unclassified') {
        if (isGiftCard) return false;
        const ipfFamilies = ['knee','singlets','belts','wrist','meet','socks','ipf-underwear'];
        return !ipfFamilies.some(family => productInGroup(p, family));
      }
      const anyRange = values => p.variants.some(v => values.some(value => rangeMatches(p, v, value)));
      const isAccessoryBag = type === 'bag' || type.includes('gym bag') || (t.includes('bag') && !t.includes('keychain'));
      const isAccessoryBottles = type.includes('bottles & shakers') || type.includes('bottle') || type.includes('shaker') || p.tagSet.has('bottles-shakers');
      const isAccessoryCompression = type.includes('compression pants') || t.includes('compression pants');
      const isAccessoryDogToy = type.includes('dog toy') || t.includes('dog toy');
      const isAccessoryHat = type === 'hats' || type.includes('hat') || t.includes('beanie') || t.includes('dad hat');
      const isAccessoryHeadband = type.includes('headband') || t.includes('headband');
      const isAccessoryStrap = type === 'straps' || type.includes('lifting strap') || t.includes('pull strap') || t.includes('lifting strap');
      const isAccessoryElbowSleeve = type.includes('elbow sleeve') || t.includes('elbow sleeve');
      const isAccessoryStrongmanSleeve = type.includes('strongman sleeve') || isAccessoryElbowSleeve || t.includes('strongman knee sleeve');
      const isAccessorySlipper = type.includes('slipper') || t.includes('slipper');
      if (key === 'accessories-bag') return isAccessoryBag;
      if (key === 'accessories-bottles') return isAccessoryBottles;
      if (key === 'accessories-compression') return isAccessoryCompression;
      if (key === 'accessories-dog-toys') return isAccessoryDogToy;
      if (key === 'accessories-hats') return isAccessoryHat && !isAccessoryHeadband;
      if (key === 'accessories-headbands') return isAccessoryHeadband;
      if (key === 'accessories-headwear-all') return anyRange(['accessories-hats','accessories-headbands']);
      if (key === 'accessories-socks') return isSock;
      if (key === 'accessories-slippers') return isAccessorySlipper;
      if (key === 'accessories-elbow-sleeves') return isAccessoryElbowSleeve;
      if (key === 'accessories-straps') return isAccessoryStrap;
      if (key === 'accessories-lifting-all') return anyRange(['accessories-elbow-sleeves','accessories-straps']);
      if (key === 'accessories-strongman-sleeves') return isAccessoryStrongmanSleeve;
      if (key === 'accessories-unclassified') {
        if (isGiftCard) return false;
        const accessoryFamilies = ['accessories-bag','accessories-bottles','accessories-compression','accessories-dog-toys','accessories-hats','accessories-headbands','accessories-socks','accessories-slippers','accessories-elbow-sleeves','accessories-straps'];
        return !accessoryFamilies.some(family => productInGroup(p, family));
      }
      if (key === 'universal-mens') {
        const g = productGender(p);
        return g === 'men' || g === 'unisex' || p.tagSet.has('mensall');
      }
      if (key === 'universal-womens') {
        const g = productGender(p);
        return g === 'women' || g === 'unisex' || p.tagSet.has('womensall');
      }
      if (key === 'universal-ipf') {
        return p.tagSet.has('ipf') || p.tagSet.has('ipfnew') || type.includes('ipf approved') || t.includes('ipf approved');
      }
      if (key === 'universal-accessories') {
        return isAccessoryBag || isAccessoryBottles || isAccessoryCompression || isAccessoryDogToy || isAccessoryHat || isAccessoryHeadband || isAccessoryElbowSleeve || isAccessoryStrap || isAccessorySlipper || isSock || isUnderwear;
      }
      if (key === 'universal-unclassified') {
        if (isGiftCard) return false;
        const universalRanges = [
          'universal-mens-tops','universal-mens-shorts','universal-mens-hoodies','universal-mens-bottoms','universal-mens-strongman','universal-mens-organic','universal-mens-underwear',
          'universal-womens-tops','universal-womens-sports-crops','universal-womens-shorts','universal-womens-hoodies','universal-womens-bottoms','universal-womens-strongwoman','universal-womens-organic',
          'universal-ipf-knee','universal-ipf-singlets','universal-ipf-belts','universal-ipf-wrist','universal-ipf-meet','universal-ipf-socks','universal-ipf-underwear',
          'universal-accessories-bag','universal-accessories-bottles','universal-accessories-compression','universal-accessories-dog-toys','universal-accessories-headwear','universal-accessories-socks','universal-accessories-slippers','universal-accessories-lifting','universal-accessories-underwear'
        ];
        return !p.variants.some(v => universalRanges.some(value => rangeMatches(p, v, value)));
      }
      if (key === 'womens-tops-all') return anyRange(['womens-bg-shirt','womens-bg-crop','womens-nbg-overfit','womens-nbg-truefit','womens-nbg-meet','womens-nbg-crop','womens-nbg-long-sleeve']);
      if (key === 'womens-shorts-all') return anyRange(['womens-shorts-kwd','womens-shorts-1z','womens-shorts-regular','womens-shorts-organic']);
      if (key === 'womens-hoodies-all') return anyRange(['womens-hoodie-bargrip','womens-hoodie-nonbar','womens-hoodie-organic','womens-hoodie-zip','womens-hoodie-crew']);
      if (key === 'womens-bottoms-all') return anyRange(['womens-bottoms-defy','womens-bottoms-organic','womens-bottoms-cobra','womens-bottoms-flexure','womens-bottoms-script','womens-bottoms-leggings']);
      if (key === 'womens-strongwoman-all') return anyRange(['womens-strongwoman-bargrip','womens-strongwoman-belts','womens-strongwoman-knee','womens-strongwoman-sleeves']);
      // Women's launch collections keep IPF belts and knee sleeves in their own
      // top-level IPF families, so Strongwoman only contains true strongwoman items.
      if (key === 'womens-strongwoman-launch') return anyRange(['womens-strongwoman-bargrip','womens-strongwoman-sleeves']);
      if (key === 'womens-organic-all') return anyRange(['womens-organic-shirts','womens-organic-hoodies','womens-organic-shorts','womens-organic-joggers']);
      if (key === 'womens-sports-crops-all') return anyRange(['womens-sports-crop-xo']);
      if (key === 'womens-unclassified') {
        if (isGiftCard) return false;
        const womensFamilies = [
          'womens-tops-all','womens-shorts-all','womens-hoodies-all','womens-bottoms-all',
          'womens-strongwoman-all','womens-strongwoman-launch','womens-organic-all','womens-sports-crops-all',
          'knee','singlets','belts','wrist','meet','socks'
        ];
        return !womensFamilies.some(family => collectionGroups?.has(family) && productInGroup(p, family));
      }
      if (key === 'mens-tops-all') return anyRange(['mens-bg-shirt','mens-bg-tank','mens-nbg-overfit','mens-nbg-truefit','mens-nbg-meet','mens-nbg-polo','mens-nbg-shirts','mens-nbg-stringer','mens-nbg-long-sleeve']);
      if (key === 'mens-shorts-all') return anyRange(['mens-shorts-kwd','mens-shorts-1z','mens-shorts-regular','mens-shorts-mvn']);
      if (key === 'mens-hoodies-all') return anyRange(['mens-hoodie-bargrip','mens-hoodie-nonbar','mens-hoodie-organic','mens-hoodie-zip','mens-hoodie-crew']);
      if (key === 'mens-bottoms-all') return anyRange(['mens-bottoms-defy','mens-bottoms-organic','mens-bottoms-cobra','mens-bottoms-compression','mens-bottoms-script']);
      if (key === 'mens-strongman-all') return anyRange(['mens-strongman-bargrip','mens-strongman-belts','mens-strongman-knee','mens-strongman-sleeves']);
      // On Men's launch/collection pages, Strongman is intentionally limited to
      // true Strongman-specific products. IPF belts and IPF knee sleeves have their
      // own correctly named top-level families on those pages.
      if (key === 'mens-strongman-launch') return anyRange(['mens-strongman-bargrip','mens-strongman-sleeves']);
      if (key === 'mens-organic-all') return anyRange(['mens-organic-shirts','mens-organic-hoodies','mens-organic-shorts','mens-organic-joggers','mens-organic-underwear']);

      if (key === 'mens-underwear-all') return anyRange(['underwear-boxers']);
      if (key === 'mens-unclassified') {
        if (isGiftCard) return false;
        // Audit only against families that are actually enabled for the current
        // Men's context. This lets launch pages classify IPF singlets/knee sleeves
        // correctly without hiding them from the stricter Men's All QA check.
        const mensFamilies = [
          'mens-tops-all','mens-shorts-all','mens-hoodies-all','mens-bottoms-all',
          'mens-strongman-all','mens-strongman-launch','mens-underwear-all',
          'knee','singlets','belts','wrist','meet','socks'
        ];
        return !mensFamilies.some(family => collectionGroups?.has(family) && productInGroup(p, family));
      }
      return true;
    }

    const genderGroup = root.querySelector('[data-a7-gender-group]');
    const sizeGroup = root.querySelector('[data-a7-size-group]');
    const lengthGroup = root.querySelector('[data-a7-length-group]');
    const thicknessGroup = root.querySelector('[data-a7-thickness-group]');
    const contextualColourGroup = root.querySelector('[data-a7-colour-context-group]');
    const globalColourGroup = root.querySelector('[data-a7-colour-global-group]');
    root.querySelectorAll('[data-a7-colour-list]').forEach(colourListNode => {
      [...colourListNode.querySelectorAll('[data-a7-colour-choice]')]
        .sort((a, b) => Number(a.dataset.a7ColourOrder || 999) - Number(b.dataset.a7ColourOrder || 999))
        .forEach(node => colourListNode.appendChild(node));
    });
    const contextualGroups = { gender: genderGroup, size: sizeGroup, length: lengthGroup, thickness: thicknessGroup, colour: contextualColourGroup };
    const contextualByFamily = {
      knee: ['size','colour'],
      singlets: ['gender','size','colour'],
      belts: ['thickness','size','colour'],
      wrist: ['length','colour'],
      meet: ['size','colour'],
      socks: ['size','colour'],
      'ipf-underwear': ['size','colour'],
      'accessories-bag': ['size','colour'],
      'accessories-bottles': ['size','colour'],
      'accessories-compression': ['size','colour'],
      'accessories-dog-toys': ['size','colour'],
      'accessories-headwear-all': ['size','colour'],
      'accessories-hats': ['size','colour'],
      'accessories-headbands': ['size','colour'],
      'accessories-socks': ['size','colour'],
      'accessories-slippers': ['size','colour'],
      'accessories-lifting-all': ['size','colour'],
      'accessories-elbow-sleeves': ['size','colour'],
      'accessories-straps': ['size','colour'],
      'accessories-unclassified': [],
      'universal-mens': ['size','colour'],
      'universal-womens': ['size','colour'],
      'universal-ipf': ['size','colour'],
      'universal-accessories': ['size','colour'],
      'universal-unclassified': [],
      'womens-tops-all': ['size','colour'],
      'womens-shorts-all': ['size','colour'],
      'womens-hoodies-all': ['size','colour'],
      'womens-bottoms-all': ['size','colour'],
      'womens-strongwoman-all': ['size','colour'],
      'womens-strongwoman-launch': ['size','colour'],
      'womens-organic-all': ['size','colour'],
      'womens-sports-crops-all': ['size','colour'],
      'womens-unclassified': [],
      'mens-tops-all': ['size','colour'],
      'mens-shorts-all': ['size','colour'],
      'mens-hoodies-all': ['size','colour'],
      'mens-bottoms-all': ['size','colour'],
      'mens-strongman-all': ['size','colour'],
      'mens-strongman-launch': ['size','colour'],
      'mens-organic-all': ['size','colour'],
      'mens-underwear-all': ['size','colour'],
      'mens-unclassified': [],
      'ipf-unclassified': []
    };
    let contextStaging = null;

    function clearContextualSelections() {
      inputs.forEach(input => {
        const key = input.dataset.a7FilterInput;
        // Colour is intentionally global in V48. Keep it when switching or
        // closing a family so a customer can move from, for example, Blue
        // Knee Sleeves to all Blue IPF products without reselecting Blue.
        if (['range','gender','size','length','thickness'].includes(key)) input.checked = false;
      });
    }

    function syncIpfHubUi() {
      if (!isContextHub) return;
      Object.values(contextualGroups).forEach(node => { if (node && contextStaging) contextStaging.appendChild(node); });

      root.querySelectorAll('[data-a7-range-group]').forEach(group => {
        const key = group.dataset.a7GroupKey || '';
        if (!collectionGroups.has(key)) return;
        const selected = activeFamily === key;
        group.classList.toggle('is-family-selected', selected);
        group.open = selected;
        group.querySelector('[data-a7-family-box]')?.classList.toggle('is-selected', selected);
        group.querySelector('[data-a7-family-title]')?.setAttribute('aria-current', selected ? 'true' : 'false');
      });

      if (activeFamily) {
        const target = root.querySelector(`[data-a7-range-group][data-a7-group-key="${activeFamily}"]`);
        const slot = target?.querySelector('[data-a7-context-slot]');
        (contextualByFamily[activeFamily] || []).forEach(key => {
          const node = contextualGroups[key];
          if (node && slot) {
            slot.appendChild(node);
            node.open = true;
          }
        });
      }
    }

    function setActiveFamily(key, { preserveSelections=false, skipApply=false } = {}) {
      if (!isContextHub) return;
      const changing = activeFamily !== key;
      if (changing && !preserveSelections) clearContextualSelections();
      activeFamily = key || '';
      syncIpfHubUi();
      updateSizeVisibility();
      updateLengthVisibility();
      updateThicknessVisibility();
      if (!skipApply) apply();
    }

    function setupIpfHub() {
      if (!isContextHub) return;
      root.classList.add('is-ipf-hub');
      if (isMensHub) root.classList.add('is-mens-hub');
      if (isWomensHub) root.classList.add('is-womens-hub');
      if (isAccessoriesHub) root.classList.add('is-accessories-hub');
      if (isUniversalHub) root.classList.add('is-universal-hub');
      contextStaging = document.createElement('div');
      contextStaging.hidden = true;
      contextStaging.setAttribute('data-a7-context-staging', '');
      panel.appendChild(contextStaging);

      const summary = root.querySelector('.a7-filter__result-summary');
      if (summary) {
        const intro = document.createElement('div');
        intro.className = 'a7-filter__hub-intro';
        intro.textContent = (isMensHub || isWomensHub || isAccessoriesHub || isUniversalHub) ? 'Choose a section to reveal its relevant filters.' : 'Choose a product type to reveal its relevant filters.';
        summary.insertAdjacentElement('afterend', intro);
      }

      root.querySelectorAll('[data-a7-range-group]').forEach(group => {
        const key = group.dataset.a7GroupKey || '';
        if (!collectionGroups.has(key)) return;
        group.open = false;
        group.classList.add('a7-filter__family-card');
        const summaryEl = group.querySelector(':scope > summary');
        const body = group.querySelector(':scope > .a7-filter__group-body');
        if (!summaryEl || !body) return;

        const title = summaryEl.textContent.trim();
        summaryEl.textContent = '';
        summaryEl.classList.add('a7-filter__family-summary');
        const box = document.createElement('span');
        box.className = 'a7-filter__family-box';
        box.setAttribute('data-a7-family-box', '');
        box.setAttribute('aria-hidden', 'true');
        const titleEl = document.createElement('span');
        titleEl.className = 'a7-filter__family-title';
        titleEl.setAttribute('data-a7-family-title', '');
        titleEl.textContent = title;
        summaryEl.append(box, titleEl);

        let slot = body.querySelector('[data-a7-context-slot]');
        if (!slot) {
          slot = document.createElement('div');
          slot.className = 'a7-filter__context-slot';
          slot.setAttribute('data-a7-context-slot', '');
          body.appendChild(slot);
        }

        summaryEl.addEventListener('click', e => {
          e.preventDefault();
          const isAlreadySelected = activeFamily === key;
          if (isAlreadySelected) {
            clearContextualSelections();
            setActiveFamily('', { preserveSelections:true });
          } else {
            setActiveFamily(key);
          }
        });
      });

      syncIpfHubUi();
    }

    if (genderGroup && !isContextHub) {
      const showGender = Boolean(collectionGroups && collectionGroups.size === 1 && collectionGroups.has('singlets'));
      genderGroup.style.display = showGender ? '' : 'none';
    }
    // Simple/direct collection pages use only the global Colour row. Hub pages
    // also get a second copy embedded inside the selected family.
    if (contextualColourGroup && !isContextHub) contextualColourGroup.style.display = 'none';

    setupIpfHub();

    function matchingInputs(key, value) {
      return inputs.filter(i => i.dataset.a7FilterInput === key && i.value === value);
    }

    function syncDuplicateColourInputs(source) {
      if (source.dataset.a7FilterInput !== 'colour') return;
      matchingInputs('colour', source.value).forEach(input => {
        if (input !== source) input.checked = source.checked;
      });
    }

    function stateFromInputs() {
      ['range','gender','size','length','thickness','colour','price'].forEach(k => state[k].clear());
      inputs.forEach(input => {
        if (input.checked && state[input.dataset.a7FilterInput]) state[input.dataset.a7FilterInput].add(input.value);
      });
      state.includeSoldOut = Boolean(includeSoldOut?.checked);
    }

    function coreVariantMatches(v, testState, ignoreKey='') {
      if (!testState.includeSoldOut && !v.available) return false;
      if (ignoreKey !== 'size' && testState.size.size && !testState.size.has(v.size)) return false;
      if (ignoreKey !== 'length' && testState.length.size && !testState.length.has(v.length)) return false;
      if (ignoreKey !== 'thickness' && testState.thickness.size && !testState.thickness.has(v.thickness)) return false;
      if (ignoreKey !== 'price' && testState.price.size && !testState.price.has(priceBucket(v.price))) return false;
      return true;
    }

    function selectedRangeMatches(p, v, testState, ignoreKey='') {
      if (ignoreKey === 'range' || !testState.range.size) return true;
      return [...testState.range].some(value => rangeMatches(p, v, value));
    }

    function selectedGenderMatches(p, testState, ignoreKey='') {
      if (ignoreKey === 'gender' || !testState.gender.size) return true;
      return [...testState.gender].some(value => genderMatches(p, value));
    }

    function productMatches(p, testState=state, ignoreKey='') {
      const isGiftCard = p.data?.handle === 'gift-card' || p.typeN === 'gift card';
      if (isGiftCard) {
        if (activeFamily === 'mens-unclassified' || activeFamily === 'womens-unclassified' || activeFamily === 'ipf-unclassified' || activeFamily === 'accessories-unclassified' || activeFamily === 'universal-unclassified') return null;
        return p.variants[0] || null;
      }
      if (p.extraFamily && activeFamily !== p.extraFamily) return null;
      if (activeFamily && !productInGroup(p, activeFamily)) return null;
      if (ignoreKey !== 'colour' && testState.colour.size && !p.colours.some(c => testState.colour.has(c))) return null;
      if (!selectedGenderMatches(p, testState, ignoreKey)) return null;
      return p.variants.find(v => coreVariantMatches(v, testState, ignoreKey) && selectedRangeMatches(p, v, testState, ignoreKey)) || null;
    }

    function optionCount(key, value) {
      let total = 0;
      cards.forEach(p => {
        if (activeFamily && !productInGroup(p, activeFamily)) return;
        if (key === 'colour') {
          if (!p.colours.includes(value)) return;
          if (productMatches(p, state, 'colour')) total++;
          return;
        }

        if (key === 'gender') {
          if (!genderMatches(p, value)) return;
          if (productMatches(p, state, 'gender')) total++;
          return;
        }

        if (!selectedGenderMatches(p, state)) return;

        const match = p.variants.some(v => {
          if (!coreVariantMatches(v, state, key)) return false;
          if (key !== 'range' && !selectedRangeMatches(p, v, state)) return false;
          if (key === 'range') return rangeMatches(p, v, value);
          if (key === 'size') return v.size === value;
          if (key === 'length') return v.length === value;
          if (key === 'thickness') return v.thickness === value;
          if (key === 'price') return priceBucket(v.price) === value;
          return false;
        });

        if (match && (!state.colour.size || p.colours.some(c => state.colour.has(c)))) total++;
      });
      return total;
    }

    function rangeExists(value) {
      return cards.some(p => p.variants.some(v => rangeMatches(p, v, value)));
    }

    function sizeExists(value) {
      return cards.some(p => (!activeFamily || productInGroup(p, activeFamily)) && p.variants.some(v => v.size === value));
    }

    function lengthExists(value) {
      return cards.some(p => (!activeFamily || productInGroup(p, activeFamily)) && p.variants.some(v => v.length === value));
    }

    function thicknessExists(value) {
      return cards.some(p => (!activeFamily || productInGroup(p, activeFamily)) && p.variants.some(v => v.thickness === value));
    }

    function setLinkVariant(p, variant) {
      p.originalLinks.forEach((original, link) => {
        if (!original) return;
        if (variant?.id) {
          const url = new URL(original, window.location.origin);
          url.searchParams.set('variant', variant.id);
          link.setAttribute('href', url.pathname + url.search + url.hash);
        } else {
          link.setAttribute('href', original);
        }
      });
    }

    function updateRangeVisibility() {
      root.querySelectorAll('[data-a7-range-group]').forEach(group => {
        const key = group.dataset.a7GroupKey || '';
        const allowedByContext = !collectionGroups || collectionGroups.has(key);

        // Do not evaluate the dozens of taxonomy groups that are irrelevant to
        // this collection. As Women's/Accessories/Homepage routing grew, V42
        // was still calculating every hidden group on IPF and Men's pages.
        if (!allowedByContext) {
          group.style.display = 'none';
          return;
        }


        // Evaluate only the options inside this allowed group.
        group.querySelectorAll('[data-a7-range-option]').forEach(wrap => {
          const value = wrap.dataset.a7RangeOption;
          wrap.style.display = rangeExists(value) ? '' : 'none';
        });

        const hasVisible = group.hasAttribute('data-a7-family-direct')
          ? cards.some(p => productInGroup(p, key))
          : [...group.querySelectorAll('[data-a7-range-option]')].some(w => w.style.display !== 'none');
        group.style.display = hasVisible ? '' : 'none';
      });
    }

    function updateSizeVisibility() {
      let any = false;
      root.querySelectorAll('[data-a7-size-option]').forEach(wrap => {
        const value = wrap.dataset.a7SizeOption;
        const exists = sizeExists(value);
        wrap.style.display = exists ? '' : 'none';
        if (exists) any = true;
      });
      const group = root.querySelector('[data-a7-size-group]');
      if (group) group.style.display = any ? '' : 'none';
    }

    function updateLengthVisibility() {
      const group = root.querySelector('[data-a7-length-group]');
      if (!group) return;
      const isWristContext = Boolean((collectionGroups && collectionGroups.size === 1 && collectionGroups.has('wrist')) || (isContextHub && activeFamily === 'wrist'));
      let any = false;
      root.querySelectorAll('[data-a7-length-option]').forEach(wrap => {
        const value = wrap.dataset.a7LengthOption;
        const exists = lengthExists(value);
        wrap.style.display = exists ? '' : 'none';
        if (exists) any = true;
      });
      group.style.display = isWristContext && any ? '' : 'none';
    }

    function updateThicknessVisibility() {
      const group = root.querySelector('[data-a7-thickness-group]');
      if (!group) return;
      const isBeltContext = Boolean((collectionGroups && collectionGroups.size === 1 && collectionGroups.has('belts')) || (isContextHub && activeFamily === 'belts'));
      let any = false;
      root.querySelectorAll('[data-a7-thickness-option]').forEach(wrap => {
        const value = wrap.dataset.a7ThicknessOption;
        const exists = thicknessExists(value);
        wrap.style.display = exists ? '' : 'none';
        if (exists) any = true;
      });
      group.style.display = isBeltContext && any ? '' : 'none';
    }

    function updateCounts() {
      counts.forEach(el => {
        const rangeGroup = el.closest('[data-a7-range-group]');
        if (rangeGroup) {
          const groupKey = rangeGroup.dataset.a7GroupKey || '';
          if (collectionGroups && !collectionGroups.has(groupKey)) return;
          // Contextual family bodies are collapsed until selected, so there is
          // no user-visible reason to calculate all their counts up front.
          if (isContextHub && !activeFamily) return;
          if (isContextHub && activeFamily && groupKey !== activeFamily) return;
        }
        const raw = el.dataset.a7OptionCount || '';
        const i = raw.indexOf(':');
        if (i < 0) return;
        const key = raw.slice(0, i);
        const value = raw.slice(i + 1);
        const total = optionCount(key, value);
        el.textContent = total ? total : '0';
        const wrap = el.closest('[data-a7-choice-wrap]');
        const input = wrap?.querySelector('input');
        if (wrap) wrap.classList.toggle('is-disabled', total === 0 && !input?.checked);
      });

      root.querySelectorAll('[data-a7-filter-input="size"]').forEach(input => {
        const total = optionCount('size', input.value);
        const wrap = input.closest('[data-a7-choice-wrap]');
        if (wrap) wrap.classList.toggle('is-disabled', total === 0 && !input.checked);
      });

      root.querySelectorAll('[data-a7-filter-input="length"]').forEach(input => {
        const total = optionCount('length', input.value);
        const wrap = input.closest('[data-a7-choice-wrap]');
        if (wrap) wrap.classList.toggle('is-disabled', total === 0 && !input.checked);
      });

      root.querySelectorAll('[data-a7-filter-input="thickness"]').forEach(input => {
        const total = optionCount('thickness', input.value);
        const wrap = input.closest('[data-a7-choice-wrap]');
        if (wrap) wrap.classList.toggle('is-disabled', total === 0 && !input.checked);
      });

      let anyContextColour = false;
      let anyGlobalColour = false;
      const globalAvailableColours = new Set();
      const contextAvailableColours = new Set();
      cards.forEach(p => {
        p.colours.forEach(colour => globalAvailableColours.add(colour));
        if (!activeFamily || productInGroup(p, activeFamily)) {
          p.colours.forEach(colour => contextAvailableColours.add(colour));
        }
      });
      root.querySelectorAll('[data-a7-colour-choice]').forEach(w => {
        const value = w.dataset.a7ColourChoice;
        const isGlobal = w.dataset.a7ColourScope === 'global';
        // Global Colour always represents the whole current collection. The
        // embedded copy reflects only the selected family.
        const exists = (isGlobal ? globalAvailableColours : contextAvailableColours).has(value);
        w.style.display = exists ? '' : 'none';
        if (exists && isGlobal) anyGlobalColour = true;
        if (exists && !isGlobal) anyContextColour = true;
      });
      if (globalColourGroup) globalColourGroup.style.display = anyGlobalColour ? '' : 'none';
      if (contextualColourGroup) {
        const showContext = isContextHub && Boolean(activeFamily) && anyContextColour;
        contextualColourGroup.style.display = showContext ? '' : 'none';
      }
    }

    function inputLabel(input) {
      const wrap = input.closest('[data-a7-choice-wrap]');
      return wrap?.querySelector('.a7-filter__choice-label')?.textContent?.trim() || input.value;
    }

    function updateChips() {
      if (!chips) return;
      chips.innerHTML = '';
      const priceLabels = { under30:'Under GBP30', '30to45':'GBP30 to GBP45', over45:'Over GBP45' };

      ['range','gender','size','length','thickness','colour','price'].forEach(key => {
        state[key].forEach(value => {
          const input = inputs.find(i => i.dataset.a7FilterInput === key && i.value === value);
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'a7-filter__chip';
          let text = input ? inputLabel(input) : value;
          if (key === 'size') text = 'Size ' + value;
          if (key === 'length') text = 'Length ' + value.replace(' ', '');
          if (key === 'thickness') text = 'Thickness ' + value.replace(' ', '');
          if (key === 'colour') text = input ? inputLabel(input) : value.replaceAll('-', ' ').replace(/\b\w/g, c => c.toUpperCase());
          if (key === 'price') text = (priceLabels[value] || value).replaceAll('GBP', '£');
          b.textContent = text;
          b.addEventListener('click', () => {
            matchingInputs(key, value).forEach(match => { match.checked = false; });
            apply();
          });
          chips.appendChild(b);
        });
      });

      if (state.includeSoldOut) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'a7-filter__chip';
        b.textContent = 'Including sold out';
        b.addEventListener('click', () => { if (includeSoldOut) includeSoldOut.checked = false; apply(); });
        chips.appendChild(b);
      }
    }

    function updateUrl() {
      const url = new URL(window.location.href);
      ['a7_family','a7_range','a7_gender','a7_size','a7_length','a7_thickness','a7_colour','a7_price','a7_soldout','a7_stiffness'].forEach(k => url.searchParams.delete(k));
      const setMany = (key, set) => set.forEach(v => url.searchParams.append(key, v));
      if (isContextHub && activeFamily) url.searchParams.set('a7_family', activeFamily);
      setMany('a7_range', state.range);
      setMany('a7_gender', state.gender);
      setMany('a7_size', state.size);
      setMany('a7_length', state.length);
      setMany('a7_thickness', state.thickness);
      setMany('a7_colour', state.colour);
      setMany('a7_price', state.price);
      if (state.includeSoldOut) url.searchParams.set('a7_soldout', '1');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }

    function updateNoResults(visible) {
      if (!noResultsHost) return;
      let msg = noResultsHost.querySelector('.a7-filter__no-results');
      if (visible === 0) {
        if (!msg) {
          msg = document.createElement('div');
          msg.className = 'a7-filter__no-results';
          msg.textContent = 'No exact in-stock combinations match these filters. Try another option or clear one of your selections.';
          noResultsHost.appendChild(msg);
        }
      } else {
        msg?.remove();
      }
    }

    function apply(skipUrl=false) {
      stateFromInputs();
      let visible = 0;
      cards.forEach(p => {
        const variant = productMatches(p);
        const show = Boolean(variant);
        p.card.hidden = !show;
        if (show) visible++;
        setLinkVariant(p, show ? variant : null);
      });
      const giftCardProduct = cards.find(p => p.data?.handle === 'gift-card' || p.typeN === 'gift card');
      if (giftCardProduct && !giftCardProduct.card.hidden && giftCardProduct.card.parentNode) {
        giftCardProduct.card.parentNode.appendChild(giftCardProduct.card);
      }
      root.querySelectorAll('[data-a7-visible-count]').forEach(el => el.textContent = visible);
      updateSizeVisibility();
      updateLengthVisibility();
      updateThicknessVisibility();
      updateCounts();
      updateChips();
      updateNoResults(visible);
      if (!skipUrl) updateUrl();
    }

    function hydrateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      if (isContextHub) {
        const family = params.get('a7_family') || '';
        if (family && collectionGroups.has(family)) activeFamily = family;
      }
      const map = { a7_range:'range', a7_gender:'gender', a7_size:'size', a7_length:'length', a7_thickness:'thickness', a7_colour:'colour', a7_price:'price' };
      Object.entries(map).forEach(([param, key]) => {
        params.getAll(param).forEach(value => {
          matchingInputs(key, value).forEach(input => { input.checked = true; });
        });
      });
      if (includeSoldOut && params.get('a7_soldout') === '1') includeSoldOut.checked = true;
      if (isContextHub) syncIpfHubUi();
    }

    inputs.forEach(input => input.addEventListener('change', () => {
      if (input.dataset.a7FilterInput === 'colour') {
        // The global Colour row is deliberately collection-wide. If a family
        // was open, choosing a global colour exits that family and clears only
        // its family-specific options before applying the colour.
        const isGlobalColourInput = Boolean(input.closest('[data-a7-colour-global-group]'));
        if (isGlobalColourInput && isContextHub && activeFamily) {
          clearContextualSelections();
          setActiveFamily('', { preserveSelections:true, skipApply:true });
        }
        syncDuplicateColourInputs(input);
      }
      apply();
    }));
    includeSoldOut?.addEventListener('change', () => apply());
    root.querySelector('[data-a7-filter-clear]')?.addEventListener('click', () => {
      inputs.forEach(i => i.checked = false);
      if (includeSoldOut) includeSoldOut.checked = false;
      if (isContextHub) { activeFamily = ''; syncIpfHubUi(); }
      apply();
    });

    const open = () => {
      root.classList.add('is-open');
      document.body.classList.add('a7-filter-drawer-open');
      root.querySelector('[data-a7-filter-open]')?.setAttribute('aria-expanded', 'true');
    };
    const close = () => {
      root.classList.remove('is-open');
      document.body.classList.remove('a7-filter-drawer-open');
      root.querySelector('[data-a7-filter-open]')?.setAttribute('aria-expanded', 'false');
    };
    root.querySelector('[data-a7-filter-open]')?.addEventListener('click', open);
    root.querySelector('[data-a7-filter-overlay]')?.addEventListener('click', close);
    root.querySelectorAll('[data-a7-filter-close]').forEach(b => b.addEventListener('click', close));
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });

    root.querySelector('[data-a7-sort]')?.addEventListener('change', e => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      window.location.assign(url.toString());
    });

    updateRangeVisibility();
    hydrateFromUrl();
    apply(true);
  }

  const boot = () => document.querySelectorAll('[data-a7-filter-root]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
